/**
 * Shared helpers for working with land boundary geometry.
 *
 * Boundaries are transferred from the backend as GeoJSON geometry strings (standard [lng, lat]
 * order) so that multipart (MultiPolygon) boundaries survive intact. OpenLayers parses them and
 * handles multipart geometry natively for rendering, point matching and area.
 */
import GeoJSON from 'ol/format/GeoJSON'
import Geometry from 'ol/geom/Geometry'
import {Polygon, MultiPolygon} from 'ol/geom'
import {getArea} from 'ol/sphere'

const geoJsonFormat = new GeoJSON()

// The map/point convention used across the app: x = latitude, y = longitude.
export interface BoundaryPoint {
    x: number
    y: number
}

/**
 * Parse a boundary GeoJSON geometry string into an OpenLayers geometry.
 * Coordinates are kept in EPSG:4326 (lon/lat degrees). Returns null for empty/invalid input.
 */
export function parseBoundaryGeometry(geoJson: string | undefined | null): Geometry | null {
    if (!geoJson) return null
    try {
        return geoJsonFormat.readGeometry(geoJson)
    } catch {
        return null
    }
}

/** Parse a map of landId -> GeoJSON string into landId -> OpenLayers geometry (skips invalid). */
export function parseBoundaryMap(boundaryMap: Record<number, string>): Record<number, Geometry> {
    const result: Record<number, Geometry> = {}
    for (const [id, geoJson] of Object.entries(boundaryMap)) {
        const geometry = parseBoundaryGeometry(geoJson)
        if (geometry) result[parseInt(id)] = geometry
    }
    return result
}

/**
 * Outer ring(s) of a boundary geometry in {x: lat, y: lng} form for GeoMap rendering.
 * A MultiPolygon returns one ring per part so every part of a multipart boundary is drawn.
 */
export function boundaryRings(geometry: Geometry): BoundaryPoint[][] {
    const toRing = (ring: number[][]): BoundaryPoint[] =>
        ring.map(c => ({x: c[1]!, y: c[0]!})) // GeoJSON [lng, lat] -> {x: lat, y: lng}

    if (geometry instanceof Polygon) {
        const outer = geometry.getCoordinates()[0]
        return outer ? [toRing(outer)] : []
    }
    if (geometry instanceof MultiPolygon) {
        return geometry.getCoordinates()
            .map(part => part[0])
            .filter((ring): ring is number[][] => !!ring)
            .map(toRing)
    }
    return []
}

/** Flatten several land geometries into a single list of rings for GeoMap's multipleBoundaries prop. */
export function boundariesToRings(geometries: Geometry[]): BoundaryPoint[][] {
    return geometries.flatMap(boundaryRings).filter(ring => ring.length > 2)
}

/** Geodesic area of a boundary geometry in hectares (multipart-aware; holes subtracted). */
export function boundaryAreaHectares(geometry: Geometry | null): number | null {
    if (!geometry) return null
    return getArea(geometry, {projection: 'EPSG:4326'}) / 10000
}

/** True if the (lat, lng) point falls inside any part of the boundary geometry. */
export function pointInBoundary(geometry: Geometry, latitude: number, longitude: number): boolean {
    return geometry.intersectsCoordinate([longitude, latitude])
}
